import { NavLink } from 'react-router';
import {
  Inbox,
  LayoutDashboard,
  Network,
  CloudCog,
  KeyRound,
  Cable,
  Radio,
  Map,
  Siren,
  BellRing,
  GitPullRequest,
  Building2,
  Gauge,
  Settings,
  Lock,
  Waypoints,
  Grid3x3,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth.ts';
import { useFeatureFlags } from '@/hooks/useCapabilities.ts';
import { useRollouts, ROLLOUTS, type RolloutFlag } from '@/hooks/useRollout.ts';
import { useIsMsp } from '@/hooks/useIsMsp.ts';
import { usePermission } from '@/hooks/usePermission.ts';
import type { FeatureKey } from '@/features/registry.ts';
import { cn } from '@/lib/utils.ts';
import { config } from '@/config';

/**
 * SideNav — primary navigation, rendered by AppShell.
 *
 * Layout strategy: the same JSX renders both as the persistent desktop
 * <aside> and as the mobile <Sheet> body. AppShell selects which is
 * visible at a given breakpoint via tailwind utilities.
 *
 * Sections mirror the existing Bulma sidenav (Main / Planning /
 * Monitoring / Administration) so muscle memory survives the migration.
 * Per addendum Q-A6 default: the inventory entry is `/network`
 * (unified). The old /devices and /endpoints list pages are gone —
 * both redirect into /network (equipment tab = management surface);
 * only the /devices/:id and /endpoints/:id detail routes remain.
 *
 * Active-route highlighting: react-router's <NavLink> auto-applies the
 * `active` className. `end` is set on Home so it doesn't match every
 * child route.
 *
 * Spec §5 touch targets: every item is min-h-11 (44px). The grouping
 * labels and dividers are non-interactive so they don't need targets.
 */

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Pass to <NavLink end> for routes that shouldn't match descendants (e.g. `/`). */
  end?: boolean;
  /** SaaS-only feature gate. When the edition/plan lacks it, the item shows a
   *  lock badge and the route renders a teaser. */
  feature?: FeatureKey;
  /** Beta gate. When the flag is not rolled out to the organization the item is
   *  hidden outright — no lock, since there is nothing to buy. */
  rollout?: RolloutFlag;
}

const SECTIONS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Main',
    items: [
      { to: '/home', label: 'Home', icon: LayoutDashboard },
      { to: '/network', label: 'My Network', icon: Network },
      { to: '/gateways', label: 'Gateways', icon: CloudCog },
      // Everyone's: every user sees their own sessions and devices here.
      { to: '/vpn', label: 'VPN', icon: ShieldCheck },
      { to: '/credentials', label: 'Credentials', icon: KeyRound },
      { to: '/controllers', label: 'Controllers', icon: Radio },
      { to: '/transport-definitions', label: 'Transports', icon: Cable },
      { to: '/views', label: 'Views', icon: Map },
    ],
  },
  {
    label: 'Monitoring',
    items: [
      // Plans are built and run inside CMs, so there's no standalone plan-list link;
      // the /network-plans pages remain reachable via deep links from a CM.
      { to: '/change-management', label: 'Change Management', icon: GitPullRequest, feature: 'changeManagement' },
      { to: '/alerts', label: 'Alerts', icon: Siren },
      { to: '/incidents', label: 'Incidents', icon: BellRing },
      // `end` so the inbox highlights on /notifications alone and not on every
      // /notifications/* child, which each have their own entry in that area's nav.
      { to: '/notifications', label: 'Notifications', icon: Inbox, end: true },
      // S0 skeleton (dev only): DEV is statically false in built bundles.
      { to: '/segments', label: 'Segments', icon: Grid3x3 },
      ...(import.meta.env.DEV ? [{ to: '/path-check', label: 'Path Check', icon: Waypoints }] : []),
      { to: '/segmentation', label: 'Segmentation', icon: Grid3x3, rollout: ROLLOUTS.segmentationMatrix },
    ],
  },
];

export function SideNav() {
  const { user } = useAuth();
  const hasFeature = useFeatureFlags();
  const rolledOut = useRollouts();
  const isMsp = useIsMsp();
  const hasAdminAccess = !!user?.permissions?.length;
  const canReadMspCustomers = usePermission('msp_customer:read', 'organization');

  // Customers is a top-level section for an MSP — the daily workflow, not an
  // admin concern — but only for an MSP: gated on both the org's MSP mode and the
  // commercial `msp_customer:read` permission, never `division:read`. Hidden
  // outright rather than lock-badged, since MSP mode is a self-service toggle
  // rather than a plan upsell, so a locked link would point at nothing to buy.
  // Every `/api/msp/customers` route authorizes `msp_customer:read` against the
  // organization, so a division administrator holding the name still gets a 403.
  // Offering the section anyway was a nav entry that could only fail.
  const showCustomers = isMsp && canReadMspCustomers;
  const sections: { label: string; items: NavItem[] }[] = [
    ...SECTIONS,
    ...(showCustomers
      ? [
          {
            label: 'Customers',
            items: [
              { to: '/customers/dashboard', label: 'Dashboard', icon: Gauge },
              // `end` so the exact list route highlights here without also lighting
              // up on the dashboard child path; a detail page (`/customers/:id`)
              // then highlights neither, which the breadcrumb covers.
              { to: '/customers', label: 'All customers', icon: Building2, end: true },
            ],
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col h-full w-full">
      {/* Brand row. No close button here — when SideNav renders inside
          the mobile Sheet, the Sheet primitive provides its own X in
          the top-right corner. Two close buttons would be a UX bug.
          When SideNav renders as the persistent desktop drawer there's
          nothing to close. */}
      <div className="h-14 flex items-center px-4 border-b border-border flex-shrink-0">
        <NavLink to="/home" className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold flex-shrink-0">
            {config.appName.charAt(0)}
          </div>
          <span className="font-semibold tracking-tight text-[15px] truncate">{config.appName}</span>
        </NavLink>
      </div>

      <nav className="flex-1 px-2 py-3 overflow-y-auto" aria-label="Main">
        {sections.map((section) => (
          <div key={section.label} className="mb-3 last:mb-0">
            <div className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              {section.label}
            </div>
            <ul className="space-y-0.5">
              {section.items.filter((item) => !item.rollout || rolledOut(item.rollout)).map((item) => {
                const locked = item.feature ? !hasFeature(item.feature) : false;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 px-2.5 py-2 min-h-11 md:min-h-9 rounded-md text-sm transition-colors',
                          isActive
                            ? 'bg-brand-soft text-brand font-medium'
                            : 'text-foreground/80 hover:bg-accent hover:text-accent-foreground',
                        )
                      }
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                      <span className="truncate">{item.label}</span>
                      {locked ? (
                        <Lock
                          className="w-3 h-3 ml-auto flex-shrink-0 text-muted-foreground"
                          aria-label="Available in the full product"
                        />
                      ) : null}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {hasAdminAccess ? (
          <div className="mb-3 pt-2 mt-2 border-t border-border-subtle">
            <div className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              Administration
            </div>
            <ul className="space-y-0.5">
              <li>
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 px-2.5 py-2 min-h-11 md:min-h-9 rounded-md text-sm transition-colors',
                      isActive
                        ? 'bg-brand-soft text-brand font-medium'
                        : 'text-foreground/80 hover:bg-accent hover:text-accent-foreground',
                    )
                  }
                >
                  <Settings className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                  <span className="truncate">Organization Admin</span>
                </NavLink>
              </li>
            </ul>
          </div>
        ) : null}
      </nav>
    </div>
  );
}
